/**
 * Dedicated "For You" page that mirrors Jira's personalized hub.
 */
import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { forYouAPI } from '@/api/forYouAPI';
import { ProjectCard } from '@/components/ProjectCard';
import { Loader } from '@/components/Loader';
import { TaskTypeIcon } from '@/components/TaskTypeIcon';
import AvatarTooltip from '@/components/AvatarTooltip';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { commentAPI } from '@/api/commentAPI';
import { taskAPI } from '@/api/taskAPI';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { subtaskAPI } from '@/api/subtaskAPI';
import { deleteComment, formatCommentsForActivity, updateComment } from '@/utils/commentUtils';
import { API_BASE_URL } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchProjects, updateProject, deleteProject } from '@/store/projectSlice';
import { fetchRecentTasks, updateTask, updateSubtask } from '@/store/taskSlice';
import { ForYouResponse, ForYouStatusGroup, ForYouTaskSummary, Subtask } from '@/types';
import { ensureHtml, toPlainText } from '@/utils/richText';
import type { Comment as ProjectComment, CommentActivity } from '@/types/comments';
import toast from 'react-hot-toast';
import { Check, X } from 'lucide-react';

export const ForYou = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projects: storeProjects, loading: projectsLoading, currentProject } = useAppSelector((state) => state.projects);
  const { user } = useAppSelector((state) => state.auth);

  const [data, setData] = useState<ForYouResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'assigned' | 'worked'>('assigned');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Task modal state (reused from dashboard experience)
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [activity, setActivity] = useState<CommentActivity[]>([]);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [showCollaborator, setShowCollaborator] = useState<string | null>(null);
  const collaboratorRef = useRef<HTMLDivElement | null>(null);
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<any>(null);

  // Edit project state
  const [editingProject, setEditingProject] = useState<any>(null);
  const [editName, setEditName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete project state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const assignedGroups: ForYouStatusGroup[] = data?.assigned?.groups ?? [];
  const workedTasks: ForYouTaskSummary[] = data?.worked?.tasks ?? [];

  const isEmptyState = !loading && !error && assignedGroups.length === 0 && workedTasks.length === 0;

  useEffect(() => {
    if (!projectsLoading && storeProjects.length === 0) {
      dispatch(fetchProjects({ is_active: true, organization_id:user?.organization_id }) as any);
    }
  }, [dispatch, storeProjects.length, user?.organization_id]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await forYouAPI.getOverview();
      setData(response);
      setError(null);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to load personalized data.';
      setError(detail);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  const resolveTaskTypeIconUrl = (raw?: string | null) => {
    if (!raw) return null;
    const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
    if (builtInIcons.includes(raw.toLowerCase())) return null;
    try {
      if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
      const api = new URL(API_BASE_URL);
      const origin = `${api.protocol}//${api.host}`;
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return origin + path;
    } catch {
      return null;
    }
  };

  const colorFromSeed = (seed: string) => {
    const colors = ['bg-red-500', 'bg-green-500', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500'];
    const sum = Array.from(seed || 'seed').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };

  const initialsFromName = (value?: string) => {
    if (!value) return 'U';
    const parts = value.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const formatTaskKey = (task: ForYouTaskSummary) => {
    if (task.project_key && task.display_number) {
      return `${task.project_key}-${task.display_number}`;
    }
    return (task.project_key || task.id?.slice(0, 6) || 'TASK').toUpperCase();
  };

  const isSubtaskEntity = (item: any): item is Subtask => Boolean(item && item.task_id);

  const openSubtaskModal = (subtask: Subtask, parentTask: any) => {
    setParentTaskForSubtask(parentTask);
    setActiveSubtask(subtask);
    setIsTaskModalOpen(false);
    setIsSubtaskModalOpen(true);
  };

  const closeSubtaskModal = () => {
    setIsSubtaskModalOpen(false);
    setActiveSubtask(null);
    setParentTaskForSubtask(null);
  };

  const handleTaskClick = async (task: ForYouTaskSummary | Subtask) => {
    const subtaskClicked = isSubtaskEntity(task);
    try {
      if (subtaskClicked) {
        const parentId = task.task_id;
        const [parentTask, parentSubtasks] = await Promise.all([
          taskAPI.getTask(parentId),
          taskAPI.getSubtasks(parentId),
        ]);

        const resolvedProjectId = parentTask?.project_id || (task as any)?.project_id;
        if (resolvedProjectId) {
          await refreshProjectMembers(resolvedProjectId);
        }

        const matchedSubtask = parentSubtasks.find((s) => String(s.id) === String(task.id));
        openSubtaskModal((matchedSubtask as Subtask) || task, parentTask || (task as any));
        return;
      }

      if ((task as ForYouTaskSummary).project_id) {
        await refreshProjectMembers((task as ForYouTaskSummary).project_id as string);
      }
      const full = await taskAPI.getTask(task.id);
      setSelectedTask(full || task);
      await loadComments(task.id);
      setIsTaskModalOpen(true);
    } catch (err) {
      console.error('Failed to open task from For You page', err);
      toast.error('Unable to open task details right now.');
    }
  };

  const loadComments = async (taskId: string) => {
    try {
      const response = await commentAPI.getComments(taskId);
      setComments(response);
      setActivity(formatCommentsForActivity(response));
    } catch (err) {
      console.error('Failed to load comments', err);
      setComments([]);
      setActivity([]);
    }
  };

  const refreshProjectMembers = async (projectId?: string) => {
    if (!projectId) return;
    try {
      const members = await projectMemberAPI.getProjectMembers(projectId);
      setProjectMembers(members || []);
    } catch (err) {
      console.error('Failed to fetch project members', err);
    }
  };

  const handleStatusUpdate = async (taskId: string, workflowStatusId: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: workflowStatusId || undefined },
      }) as any).unwrap();
      dispatch(fetchRecentTasks({ limit: 6 }) as any);
      await loadData();
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const handleStartEdit = (project: any) => {
    setEditingProject(project);
    setEditName(project.name);
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editingProject || !editName.trim() || editName.trim().length < 3) return;
    setIsUpdating(true);
    try {
      await dispatch(updateProject({ 
        id: editingProject.id, 
        data: { name: editName.trim() } 
      }) as any).unwrap();
      setEditingProject(null);
      setEditName('');
      dispatch(fetchProjects({ is_active: true, organization_id: user?.organization_id }) as any);
      await loadData();
    } catch { } finally { setIsUpdating(false); }
  };

  const handleDeleteClick = (project: any) => {
    setShowDeleteConfirm(project);
  };

  const handleConfirmDelete = async () => {
    if (!showDeleteConfirm) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteProject(showDeleteConfirm.id) as any).unwrap();
      dispatch(fetchProjects({ is_active: true, organization_id: user?.organization_id }) as any);
      await loadData();
    } catch { } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(null);
    }
  };

  const assignedContent = useMemo(() => {
    if (!assignedGroups.length) {
      return (
        <div className="card text-center py-10 text-gray-500">
          No tasks are currently assigned to you.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {assignedGroups.map((group) => (
          <div key={group.key} className="card space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold px-3 py-1 rounded-full border border-transparent" style={{
                  backgroundColor: `${group.status_color || '#EEF2FF'}`,
                  color: group.status_color ? '#fff' : '#312E81',
                }}>{group.label}</p>
                <p className="text-sm text-gray-400">{group.tasks.length} tasks</p>
              </div>
              {group.status_color && (
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: group.status_color }} />
              )}
            </div>
            <div className="divide-y divide-gray-100">
              {group.tasks.map((task) => (
                <button
                  key={task.id}
                  className="w-full flex items-center justify-between gap-4 py-3 hover:bg-gray-50 px-1 rounded-md text-left transition"
                  onClick={() => handleTaskClick(task)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <TaskTypeIcon
                        url={resolveTaskTypeIconUrl(task.task_type_icon)}
                        name={task.task_type_name || task.task_type || 'Task'}
                        className="w-5 h-5"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-500">{formatTaskKey(task)}</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }, [assignedGroups]);

  const workedContent = useMemo(() => {
    if (!workedTasks.length) {
      return (
        <div className="card text-center py-10 text-gray-500">
          You have not collaborated on any tasks yet.
        </div>
      );
    }

    const renderParticipants = (task: ForYouTaskSummary) => {
      const participants = task.participants ?? [];
      if (!participants.length) {
        return <span className="text-xs text-gray-400">No collaborators</span>;
      }

      const visible = participants.slice(0, 5);
      const extra = participants.slice(5);

      return (
        <div className="flex items-center -space-x-2 relative">
          {visible.map((participant) => {
            const bg = colorFromSeed(participant.id || participant.name || participant.initials || 'seed');
            return (
              <AvatarTooltip key={participant.id} label={participant.name || 'User'}>
                <div
                  className={`w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-semibold text-white ${bg}`}
                >
                  {participant.avatar_url ? (
                    <img
                      src={participant.avatar_url}
                      alt={participant.name || 'User'}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    (participant.initials || initialsFromName(participant.name))
                  )}
                </div>
              </AvatarTooltip>
            );
          })}

          {extra.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCollaborator((cur) => (cur === task.id ? null : task.id));
                }}
                className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 text-xs font-semibold text-gray-700 flex items-center justify-center"
              >
                +{extra.length}
              </button>
              {showCollaborator === task.id && (
                <div
                  ref={collaboratorRef}
                  className="absolute top-10 right-0 z-20 w-52 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
                >
                  <p className="text-xs font-semibold text-gray-500 mb-2">Collaborators</p>
                  <ul className="space-y-1 max-h-48 overflow-auto">
                    {extra.map((participant) => (
                      <li key={participant.id} className="text-sm text-gray-700 flex items-center gap-2">
                        <span
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white ${colorFromSeed(participant.id || participant.name || 'seed')}`}
                        >
                          {participant.initials || initialsFromName(participant.name)}
                        </span>
                        <span className="truncate">{participant.name || 'User'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="card divide-y divide-gray-100">
        {workedTasks.map((task) => (
          <button
            key={task.id}
            className="w-full flex items-center justify-between gap-4 py-3 px-1 hover:bg-gray-50 rounded-md text-left transition"
            onClick={() => handleTaskClick(task)}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <TaskTypeIcon
                  url={resolveTaskTypeIconUrl(task.task_type_icon)}
                  name={task.task_type_name || task.task_type || 'Task'}
                  className="w-5 h-5"
                />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-500">{formatTaskKey(task)}</p>
                <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                <p className="text-xs text-gray-400 truncate">{task.project_name}</p>
              </div>
            </div>
            {renderParticipants(task)}
          </button>
        ))}
      </div>
    );
  }, [workedTasks, showCollaborator]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        collaboratorRef.current &&
        !collaboratorRef.current.contains(event.target as Node)
      ) {
        setShowCollaborator(null);
      }
    }
    if (showCollaborator !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCollaborator]);

  const renderProjects = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="card animate-pulse h-32" />
          ))}
        </div>
      );
    }

    if (!data?.projects?.length) {
      return (
        <div className="card text-center py-10 text-gray-500">
          There are no projects assigned to you yet.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.projects.map((project: any) => (
          <div key={project.id} className="relative">
            {editingProject?.id === project.id ? (
              <div className="card">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit();
                      else if (e.key === 'Escape') handleCancelEdit();
                    }}
                    className="flex-1 text-lg font-semibold px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isUpdating}
                    autoFocus
                    maxLength={200}
                  />
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={handleSaveEdit}
                      disabled={isUpdating || !editName.trim() || editName.trim().length < 3}
                      className="p-1.5 hover:bg-green-100 rounded text-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Save"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isUpdating}
                      className="p-1.5 hover:bg-red-100 rounded text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  <span className="badge badge-primary">{project.key}</span>
                </div>
              </div>
            ) : (
              <ProjectCard project={project} onEdit={handleStartEdit} onDelete={handleDeleteClick} />
            )}
          </div>
        ))}
      </div>
    );
  };

  const currentProjectKey =
    currentProject?.key ||
    storeProjects.find((p) => p.id === selectedTask?.project_id)?.key ||
    data?.projects?.find((p) => p.id === selectedTask?.project_id)?.key ||
    'PRJ';

  const projectObj = currentProject || 
  storeProjects.find((p) => p.id === selectedTask?.project_id) ||
  data?.projects?.find((p) => p.id === selectedTask?.project_id)

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">For You</h1>
          <p className="text-gray-600 mt-1">
            Quick view of everything that needs your attention across projects.
          </p>
        </div>
      </div>

      {loading && (
        <div className="py-16">
          <Loader variant="overlay" size="xl" text="Loading your workspace..." />
        </div>
      )}

      {error && !loading && (
        <div className="card border border-red-200 bg-red-50 text-red-600 p-5">
          <p className="font-semibold mb-2">We couldn’t load your data.</p>
          <p className="text-sm mb-4">{error}</p>
          <button className="btn btn-secondary" onClick={loadData}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Your projects</h2>
                <p className="text-sm text-gray-500">Projects where you are a member.</p>
              </div>
              <button
                onClick={() => navigate('/projects')}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View all →
              </button>
            </div>
            {renderProjects()}
          </section>

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">My tasks</h2>
                <p className="text-sm text-gray-500">
                  Organized by what’s assigned to you vs what you’ve collaborated on.
                </p>
              </div>
              <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-white">
                <button
                  className={`px-4 py-1.5 text-sm font-medium rounded-md ${activeTab === 'assigned' ? 'bg-primary-600 text-white shadow' : 'text-gray-600'
                    }`}
                  onClick={() => setActiveTab('assigned')}
                >
                  Assigned to me
                </button>
                <button
                  className={`px-4 py-1.5 text-sm font-medium rounded-md ${activeTab === 'worked' ? 'bg-primary-600 text-white shadow' : 'text-gray-600'
                    }`}
                  onClick={() => setActiveTab('worked')}
                >
                  Worked on
                </button>
              </div>
            </div>

            {activeTab === 'assigned' ? assignedContent : workedContent}
          </section>

          {isEmptyState && (
            <div className="card text-center py-16 text-gray-500">
              Nothing to show yet. Create a project or get added to one to start seeing updates here.
            </div>
          )}
        </>
      )}

      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setSelectedTask(null);
          setActivity([]);
          setComments([]);
        }}
        task={selectedTask}
        onStatusUpdate={async (taskId, newStatus) => {
          await handleStatusUpdate(taskId, newStatus);
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch {
            // no-op
          }
        }}
        onAddComment={async (taskId, comment, parentId) => {
          const createdComment = await commentAPI.createComment({ task_id: taskId, content: comment, parent_comment_id: parentId });
          await loadComments(taskId);
          return createdComment;
        }}
        onUpdateDescription={async (taskId, description) => {
          await dispatch(updateTask({ id: taskId, data: { description } }) as any);
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch {
            // ignore
          }
        }}
        onUpdateAssignee={async (taskId, assigneeId) => {
          await dispatch(updateTask({ id: taskId, data: { assigned_to: assigneeId === null ? null : assigneeId } }) as any);
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch {
            // ignore
          }
          if (selectedTask?.project_id) {
            await refreshProjectMembers(selectedTask.project_id);
          }
        }}
        onUpdateReporter={async (taskId, reporterId) => {
          await dispatch(updateTask({ id: taskId, data: { reporter_id: reporterId === null ? null : reporterId } }) as any);
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch {
            // ignore
          }
          if (selectedTask?.project_id) {
            await refreshProjectMembers(selectedTask.project_id);
          }
        }}
        activity={activity}
        taskDetails={selectedTask ? (() => {
          const rawLabels: any = selectedTask?.labels;
          const labels: string[] = Array.isArray(rawLabels)
            ? rawLabels
            : (typeof rawLabels === 'string'
              ? rawLabels.split(',').map((l: string) => l.trim()).filter(Boolean)
              : []);
          return {
            assignee: selectedTask.assigned_to || 'Unassigned',
            labels,
            sprint: selectedTask.sprint_id || 'Not set',
            dueDate: selectedTask.due_date || 'Not set',
          };
        })() : {}}
        projectMembers={projectMembers}
        comments={comments}
        onDeleteComment={async (commentId) => {
          await deleteComment(commentId);
          if (selectedTask) await loadComments(selectedTask.id);
        }}
        onUpdateComment={async (commentId, content) => {
          await updateComment(commentId, content);
          if (selectedTask) await loadComments(selectedTask.id);
        }}
        projectKey={currentProjectKey}
        project={projectObj}
        onUpdateTaskTitle={async (id, title) => {
          await dispatch(updateTask({ id, data: { title } }) as any);
          try {
            const fresh = await taskAPI.getTask(id);
            setSelectedTask(fresh);
          } catch {
            // ignore
          }
        }}
        onUpdateFields={async (id, updates) => {
          try {
            const fresh = await taskAPI.getTask(id);
            setSelectedTask(fresh);
          } catch {
            // ignore
          }
          if (updates.assigned_to !== undefined && selectedTask?.project_id) {
            await refreshProjectMembers(selectedTask.project_id);
          }
        }}
        onOpenSubtask={(subtask, parent) => {
          if (subtask) {
            openSubtaskModal(subtask, parent || selectedTask);
          }
        }}
      />
      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={closeSubtaskModal}
          parentKey={(() => {
            const source = parentTaskForSubtask || selectedTask;
            if (!source?.project_id) return currentProject?.key;
            const project =
              storeProjects.find((p) => p.id === source.project_id) ||
              currentProject ||
              data?.projects?.find((p) => p.id === source.project_id);
            const projectKey = project?.key || 'PRJ';
            const displayNumber = (source as any)?.display_number || (source as any)?.id?.slice(0, 6);
            return displayNumber ? `${projectKey}-${displayNumber}` : projectKey;
          })()}
          parentTitle={(parentTaskForSubtask || selectedTask)?.title}
          parentTask={(parentTaskForSubtask || selectedTask) as any}
          onOpenParent={(parent) => {
            closeSubtaskModal();
            if (parent) {
              handleTaskClick(parent);
            }
          }}
          projectMembers={projectMembers}
          activity={activity}
          projectKey={(() => {
            const source = parentTaskForSubtask || selectedTask;
            if (!source?.project_id) return currentProject?.key;
            const project =
              storeProjects.find((p) => p.id === source.project_id) ||
              currentProject ||
              data?.projects?.find((p) => p.id === source.project_id);
            return project?.key;
          })()}
          project={(() => {
            const source = parentTaskForSubtask || selectedTask;
            if (!source?.project_id) return currentProject || undefined;
            return (
              storeProjects.find((p) => p.id === source.project_id) || projectObj
            );
          })()}
          onAddComment={async (taskId, html, parentId, plainText) => {
            const contentHtml = html;
            const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
            const created = await commentAPI.createComment({
              task_id: taskId,
              content: contentText,
              content_html: contentHtml,
              content_text: contentText,
              parent_comment_id: parentId,
            });
            const parent = parentTaskForSubtask || selectedTask;
            if (parent?.id) {
              await loadComments(parent.id);
            }
            return created;
          }}
          onUpdateComment={async (commentId, html, plainText) => {
            await updateComment(commentId, html, plainText);
            const parent = parentTaskForSubtask || selectedTask;
            if (parent?.id) {
              await loadComments(parent.id);
            }
          }}
          onDeleteComment={async (commentId) => {
            await deleteComment(commentId);
            const parent = parentTaskForSubtask || selectedTask;
            if (parent?.id) {
              await loadComments(parent.id);
            }
          }}
          onSave={async (updates) => {
            if (!activeSubtask) return;
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates }) as any).unwrap();
            try {
              const fresh = await subtaskAPI.get(activeSubtask.id);
              setActiveSubtask(fresh);
            } catch (error) {
              console.error('Failed to refresh subtask:', error);
            }
          }}
          onDelete={async () => {
            setIsSubtaskModalOpen(false);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmDialog
          isOpen={!!showDeleteConfirm}
          itemName={showDeleteConfirm.name}
          itemType="project"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(null)}
          isLoading={isDeleting}
          customMessage="Are you sure you want to delete this project? This will hide the project and all its tasks, stories, epics, and issues from the system. This action cannot be undone."
        />
      )}
    </div>
  );
};

export default ForYou;

